export type LandingPagePublicationState = 'Draft' | 'Published' | 'Scheduled';
export type TagFilterMode = 'any' | 'all';

export type LandingPageBlockType =
  | 'heading'
  | 'text'
  | 'image'
  | 'gallery'
  | 'youtubevideo'
  | 'codeblock'
  | 'references';

export interface PagingResponse<T> {
  page: number;
  totalPages: number;
  pageSize: number;
  data: T[];
}

export interface SiteSummary {
  id: string;
  name: string;
  createdAt: string;
  landingPageCount: number;
  publicApiKeyCount: number;
  linkCount: number;
}

export interface ApiKeyRecord {
  id: string;
  name: string;
  siteId: string;
  keyPreview: string;
}

export interface CreatedApiKeyRecord extends ApiKeyRecord {
  key: string;
}

export interface LandingPageMetadata {
  summary: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaImageUrl: string | null;
}

export interface ReferenceLink {
  label: string;
  url: string;
}

export interface ImageAsset {
  id: string;
  siteId: string;
  originalFileName: string;
  contentType: string;
  defaultAlt: string | null;
  defaultCaption: string | null;
  createdAt: string;
  updatedAt: string;
  contentUrl: string;
  landingPageUsageCount: number;
  galleryUsageCount: number;
  canDelete: boolean;
}

export interface GallerySummary {
  id: string;
  siteId: string;
  name: string;
  imageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GalleryDetail extends Omit<GallerySummary, 'imageCount'> {
  images: ImageAsset[];
}

export interface LandingPageBlock {
  sortOrder: number;
  type: LandingPageBlockType;
  heading: string | null;
  headingLevel: number | null;
  textContent: string | null;
  imageId: string | null;
  imageAltOverride: string | null;
  imageCaptionOverride: string | null;
  youTubeUrl: string | null;
  codeLanguage: string | null;
  codeContent: string | null;
  galleryId: string | null;
  referenceLinks: ReferenceLink[];
}

export interface LandingPageSummary {
  id: string;
  siteId: string;
  title: string;
  path: string;
  summary: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  publicationState: LandingPagePublicationState;
  isPubliclyVisible: boolean;
  scheduledPublishAt: string | null;
  publishedAt: string | null;
}

export interface LandingPageDetail extends LandingPageSummary {
  metadata: LandingPageMetadata;
  blocks: LandingPageBlock[];
}

export interface LandingPageInput {
  siteId: string;
  title: string;
  path: string;
  metadata: LandingPageMetadata;
  tags: string[];
  publicationState: LandingPagePublicationState;
  scheduledPublishAt: string | null;
  publishedAt?: string | null;
  blocks: LandingPageBlock[];
}

export interface LinkSummary {
  id: string;
  siteId: string;
  url: string;
  description: string;
  headline: string | null;
  rating: number;
  tags: string[];
}

export interface LinkDetail extends LinkSummary {}

export interface LinkInput {
  siteId: string;
  url: string;
  description: string;
  headline: string | null;
  rating: number;
  tags: string[];
}

export interface BlogpostMetadata {
  summary: string;
  metaTitle: string | null;
  metaDescription: string | null;
  metaImageUrl: string | null;
}

export interface BlogpostSummary {
  id: string;
  siteId: string;
  title: string;
  summary: string;
  metadata: BlogpostMetadata;
  createdAt: string;
  updatedAt: string;
  published: boolean;
  slug: string;
  tags: string[];
}

export interface BlogpostDetail extends BlogpostSummary {
  content: string;
}

export interface BlogpostInput {
  siteId: string;
  title: string;
  metadata: BlogpostMetadata;
  content: string;
  slug: string;
  publish: boolean;
  tags: string[];
}

export interface TagFilter {
  tags: string[];
  tagMode: TagFilterMode;
}

export const ARTICLE_BLOCK_TYPES: LandingPageBlockType[] = [
  'heading',
  'text',
  'image',
  'gallery',
  'youtubevideo',
  'codeblock',
  'references',
];

export function createEmptyLandingPage(siteId: string): LandingPageInput {
  return {
    siteId,
    title: '',
    path: '',
    metadata: {
      summary: '',
      metaTitle: null,
      metaDescription: null,
      metaImageUrl: null,
    },
    tags: [],
    publicationState: 'Draft',
    scheduledPublishAt: null,
    blocks: [
      createEmptyBlock('heading', 0),
      createEmptyBlock('text', 1),
    ],
  };
}

export function createDraftLandingPage(siteId: string, now = new Date().toISOString()): LandingPageDetail {
  const landingPage = createEmptyLandingPage(siteId);

  return {
    id: 'new-landingPage',
    siteId: landingPage.siteId,
    title: landingPage.title,
    path: landingPage.path,
    summary: landingPage.metadata.summary,
    tags: [...landingPage.tags],
    createdAt: now,
    updatedAt: now,
    publicationState: landingPage.publicationState,
    isPubliclyVisible: false,
    scheduledPublishAt: landingPage.scheduledPublishAt,
    publishedAt: null,
    metadata: { ...landingPage.metadata },
    blocks: landingPage.blocks.map((block) => ({
      ...block,
      referenceLinks: block.referenceLinks.map((link) => ({ ...link })),
    })),
  };
}

export function createDraftLink(siteId: string): LinkDetail {
  return {
    id: 'new-link',
    siteId,
    url: '',
    description: '',
    headline: null,
    rating: -1,
    tags: [],
  };
}

export function createDraftBlogpost(siteId: string, now = new Date().toISOString()): BlogpostDetail {
  return {
    id: 'new-blogpost',
    siteId,
    title: '',
    summary: '',
    metadata: {
      summary: '',
      metaTitle: null,
      metaDescription: null,
      metaImageUrl: null,
    },
    content: '',
    slug: '',
    published: false,
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}

export function createEmptyBlock(type: LandingPageBlockType, sortOrder: number): LandingPageBlock {
  return {
    sortOrder,
    type,
    heading: type === 'heading' ? '' : null,
    headingLevel: null,
    textContent: type === 'text' ? '' : null,
    imageId: null,
    imageAltOverride: null,
    imageCaptionOverride: null,
    youTubeUrl: type === 'youtubevideo' ? '' : null,
    codeLanguage: type === 'codeblock' ? '' : null,
    codeContent: type === 'codeblock' ? '' : null,
    galleryId: null,
    referenceLinks:
      type === 'references'
        ? [{ label: '', url: '' }]
        : [],
  };
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function pathify(value: string): string {
  const path = slugify(value);
  return path ? `/${path}` : '/';
}

export function normalizePath(value: string): string {
  const trimmed = value.trim().replace(/\\/g, '/');
  if (!trimmed) {
    return '';
  }

  let normalized = trimmed.replace(/\/+/g, '/');
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  while (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

export function cloneLandingPage(landingPage: LandingPageDetail): LandingPageDetail {
  return {
    ...landingPage,
    tags: [...landingPage.tags],
    metadata: { ...landingPage.metadata },
    blocks: landingPage.blocks.map((block) => ({
      ...block,
      referenceLinks: block.referenceLinks.map((link) => ({ ...link })),
    })),
  };
}

export function normalizeTags(tags: readonly string[]): string[] {
  const normalizedTags: string[] = [];
  const seen = new Set<string>();

  for (const tag of tags) {
    const normalizedTag = slugify(tag);
    if (!normalizedTag) {
      continue;
    }

    if (seen.has(normalizedTag)) {
      continue;
    }

    seen.add(normalizedTag);
    normalizedTags.push(normalizedTag);
  }

  return normalizedTags;
}
